// src/components/MyProgress.js
import React, { useState, useEffect } from 'react';
import api from '../api';
import ProgressChart from './ProgressChart';
import ProgressForm from './ProgressForm';
import { IoAddCircleOutline, IoImageOutline } from 'react-icons/io5';

const METRICS = [
  { key: 'weight_kg', label: 'Peso (kg)' },
  { key: 'body_fat_percentage', label: '% Grasa corporal' },
  { key: 'waist_cm', label: 'Cintura (cm)' },
  { key: 'chest_cm', label: 'Pecho (cm)' },
  { key: 'hips_cm', label: 'Caderas (cm)' },
  { key: 'bicep_cm', label: 'Bícep (cm)' },
  { key: 'thigh_cm', label: 'Muslo (cm)' },
];

const MyProgress = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeMetric, setActiveMetric] = useState('weight_kg');

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/me/progress');
      setLogs(response.data);
    } catch (err) {
      setError('No se pudo cargar tu historial de progreso.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleLogCreated = (newLog) => {
    setLogs((prev) => [...prev, newLog].sort((a, b) => new Date(a.date) - new Date(b.date)));
  };

  const latestLog = logs.length > 0 ? logs[logs.length - 1] : null;
  const firstLog = logs.length > 0 ? logs[0] : null;
  const weightChange =
    latestLog?.weight_kg != null && firstLog?.weight_kg != null
      ? (latestLog.weight_kg - firstLog.weight_kg).toFixed(1)
      : null;

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white">
      {isFormOpen && (
        <ProgressForm onClose={() => setIsFormOpen(false)} onLogCreated={handleLogCreated} />
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Mi Progreso</h1>
          <p className="text-gray-400 mt-1">Lleva el registro de tu evolución física.</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <IoAddCircleOutline size={20} />
          Nuevo Registro
        </button>
      </div>

      {/* Stats rápidas */}
      {latestLog && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Último peso" value={latestLog.weight_kg ? `${latestLog.weight_kg} kg` : '—'} />
          <StatCard
            label="Cambio total"
            value={weightChange !== null ? `${weightChange > 0 ? '+' : ''}${weightChange} kg` : '—'}
            highlight={weightChange !== null ? (parseFloat(weightChange) <= 0 ? 'green' : 'red') : null}
          />
          <StatCard
            label="% Grasa"
            value={latestLog.body_fat_percentage ? `${latestLog.body_fat_percentage}%` : '—'}
          />
          <StatCard label="Registros totales" value={logs.length} />
        </div>
      )}

      {loading && <p className="text-gray-400 text-center py-10">Cargando tu progreso...</p>}
      {error && <p className="text-red-400 text-center py-10">{error}</p>}

      {!loading && !error && logs.length === 0 && (
        <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-12">
          <p className="text-gray-400 text-lg">Aún no tienes registros de progreso.</p>
          <button
            onClick={() => setIsFormOpen(true)}
            className="mt-4 bg-marizan-red hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Añadir mi primer registro
          </button>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <>
          {/* Selector de métrica */}
          <div className="bg-black bg-opacity-40 rounded-xl p-5 border border-gray-700 mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {METRICS.filter((m) => logs.some((l) => l[m.key] != null)).map((m) => (
                <button
                  key={m.key}
                  onClick={() => setActiveMetric(m.key)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activeMetric === m.key
                      ? 'bg-marizan-red text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <ProgressChart
              logs={logs}
              metric={activeMetric}
              label={METRICS.find((m) => m.key === activeMetric)?.label || activeMetric}
            />
          </div>

          {/* Galería de fotos */}
          {logs.some((l) => l.photo_url) && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">Fotos de progreso</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {logs
                  .filter((l) => l.photo_url)
                  .map((log) => (
                    <div key={log.id} className="relative group rounded-lg overflow-hidden aspect-square bg-gray-800">
                      <img
                        src={log.photo_url}
                        alt={`Progreso ${log.date}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-xs text-center py-1 text-gray-300">
                        {new Date(log.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Historial en tabla */}
          <div className="bg-black bg-opacity-40 rounded-xl border border-gray-700 overflow-hidden">
            <h2 className="text-xl font-semibold p-5 border-b border-gray-700">Historial de registros</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left px-5 py-3">Fecha</th>
                    <th className="text-right px-4 py-3">Peso (kg)</th>
                    <th className="text-right px-4 py-3">% Grasa</th>
                    <th className="text-right px-4 py-3">Cintura</th>
                    <th className="text-right px-4 py-3">Pecho</th>
                    <th className="text-right px-4 py-3">Foto</th>
                  </tr>
                </thead>
                <tbody>
                  {[...logs].reverse().map((log) => (
                    <tr key={log.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                      <td className="px-5 py-3 font-medium text-white">
                        {new Date(log.date + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">{log.weight_kg ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{log.body_fat_percentage != null ? `${log.body_fat_percentage}%` : '—'}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{log.waist_cm ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{log.chest_cm ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        {log.photo_url ? (
                          <a href={log.photo_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            <IoImageOutline size={18} className="inline" />
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, highlight }) => (
  <div className="bg-black bg-opacity-40 rounded-xl p-4 border border-gray-700">
    <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${highlight === 'green' ? 'text-green-400' : highlight === 'red' ? 'text-red-400' : 'text-white'}`}>
      {value}
    </p>
  </div>
);

export default MyProgress;
